import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "@/App.css";

import { SessionProvider } from "@/lib/session";
import { I18nProvider } from "@/lib/i18n";
import { getAuthToken } from "@/lib/api";
import AppLock from "@/components/AppLock";
import TopNav from "@/components/TopNav";
import NewLogin from "@/pages/NewLogin";
import Invoices from "@/pages/Invoices";
import Cashflow from "@/pages/Cashflow";
import EndShift from "@/pages/EndShift";
import LiveView from "@/pages/LiveView";
import PrivatePortal from "@/pages/PrivatePortal";
import Register from "@/pages/Register";
import SaasLogin from "@/pages/SaasLogin";
import Onboarding from "@/pages/Onboarding";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import Kronos from "@/pages/Kronos";
import Chat from "@/pages/Chat";
import Payroll from "@/pages/Payroll";
import DistributorCheques from "@/pages/DistributorCheques";
import Landing from "@/pages/Landing";
import AdminBroadcast from "@/pages/AdminBroadcast";
import AdminCPanel from "@/pages/AdminCPanel";
import EdiNotificationModal from "@/components/EdiNotificationModal";
import SystemUpdatesModal from "@/components/SystemUpdatesModal";

// Iter 71 — Global toast.error filter that swallows the
// transient "Missing bearer token" message during route transitions
// IF we still have a valid token locally. The Iter 64 axios response
// interceptor already retries those requests transparently; this is
// a belt-and-braces guard so any page-level catch block that fires
// `toast.error(e.response?.data?.detail)` faster than the retry
// resolves still keeps the viewport clean.
const _origToastError = toast.error.bind(toast);
toast.error = (msg, opts) => {
  try {
    const txt = typeof msg === "string" ? msg : (msg?.message || "");
    if (/missing bearer token/i.test(txt) && getAuthToken()) {
      return; // suppress — Iter 64 interceptor will retry
    }
  } catch (_) { /* fall through */ }
  return _origToastError(msg, opts);
};

// Public SaaS routes — NOT wrapped by AppLock so anyone on the open internet
// can sign up, sign in, finish onboarding, or pay their subscription.
// `startsWith` is used (not equality) so trailing slashes, query strings,
// and locale segments never accidentally fall through to the AppLock gate.
const PUBLIC_PREFIXES = ["/landing", "/register", "/saas-login", "/onboarding", "/billing"];
const ADMIN_PREFIXES = ["/admin/cpanel", "/admin/broadcast"];

// Iter 33 — SaaS host detection. On `kioskforce-erp.vip` the root `/`
// must serve the public Landing page (lead-gen), NOT the private
// AppLock prompt. Master hosts (`savvaspos.store`) keep their existing
// behaviour of routing `/` straight into the kiosk login screen.
const SAAS_HOSTNAMES = ["kioskforce-erp.vip", "www.kioskforce-erp.vip"];
function isSaasHost() {
  if (typeof window === "undefined") return false;
  return SAAS_HOSTNAMES.includes(window.location.hostname);
}

function isPublicPath(pathname) {
  if (!pathname) return false;
  // Normalise — strip trailing slash and lowercase the prefix segment so
  // /Register, /register/, /register?ref=foo all resolve as public.
  const path = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  // SaaS host: bare root is also public (serves Landing).
  if (path === "/" && isSaasHost()) return true;
  return PUBLIC_PREFIXES.some(
    (p) => path === p || path.startsWith(p + "/"),
  );
}

function isAdminPath(pathname) {
  // Iter 111 · Admin CPanel + Broadcast routes bypass the AppLock kiosk
  // gate (they enforce role via JWT). Staff-tier `savvaspos/store` should
  // NOT block a platform admin from `/admin/cpanel`.
  if (!pathname) return false;
  const path = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  return ADMIN_PREFIXES.some(
    (p) => path === p || path.startsWith(p + "/"),
  );
}

function KioskShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <TopNav />
      <main className="pt-24 px-4 sm:px-6 lg:px-10 pb-10 max-w-[1920px] mx-auto">
        {children}
      </main>
    </div>
  );
}

function RouteSwitch() {
  const loc = useLocation();
  if (isPublicPath(loc.pathname)) {
    // Public SaaS landing — render directly, no AppLock, no TopNav. The
    // catch-all inside this block keeps unknown public-prefix paths public
    // instead of bouncing them back into the AppLock-protected tree.
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/landing" element={<Landing />} />
        <Route path="/register" element={<Register />} />
        <Route path="/saas-login" element={<SaasLogin />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    );
  }
  if (isAdminPath(loc.pathname)) {
    // Iter 111 · Admin routes render outside AppLock — protected by
    // JWT + role gate on the backend. This lets a platform admin log in
    // via /saas-login and jump straight to /admin/cpanel without needing
    // the kiosk staff AppLock credentials.
    return (
      <Routes>
        <Route path="/admin/cpanel" element={<AdminCPanel />} />
        <Route path="/admin/broadcast" element={<AdminBroadcast />} />
        <Route path="*" element={<Navigate to="/admin/cpanel" replace />} />
      </Routes>
    );
  }
  // Private kiosk app — AppLock-wrapped, SessionProvider-scoped.
  return (
    <AppLock>
      <SessionProvider>
        <KioskShell>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<NewLogin />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/cashflow" element={<Cashflow />} />
            <Route path="/end-shift" element={<EndShift />} />
            <Route path="/live-view" element={<LiveView />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/kronos" element={<Kronos />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/payroll" element={<Payroll />} />
            <Route path="/cheques" element={<DistributorCheques />} />
            <Route path="/portal" element={<PrivatePortal />} />
            <Route path="/admin/broadcast" element={<AdminBroadcast />} />
            <Route path="/admin/cpanel" element={<AdminCPanel />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <EdiNotificationModal />
          <SystemUpdatesModal />
        </KioskShell>
      </SessionProvider>
    </AppLock>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <RouteSwitch />
        <Toaster position="top-center" richColors closeButton />
      </I18nProvider>
    </BrowserRouter>
  );
}
